import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { datadogRum } from '@datadog/browser-rum';
import { BaseSyncUtil, API_ROOT } from './BaseSyncUtil';
import LocalStorageManagerV1 from './LocalStorageManagerV1';
const API_RESUME_ROOT = `${API_ROOT}resumes/`;
const API_SETTINGS_ROOT = `${API_ROOT}settings/`;
export default class RemoteSyncUtil extends BaseSyncUtil {
    static _instance;
    syncPromise;
    readyPromise;
    cachedRemoteResumes;
    cachedRemoteSettings;
    static instance() {
        return this._instance || (this._instance = new RemoteSyncUtil());
    }
    async ready() {
        if (!this.readyPromise) {
            this.readyPromise = this.init();
        }
        await this.readyPromise;
    }
    async sync() {
        await this.ready();
        try {
            if (this.getState() !== 'syncing') {
                this.setState('syncing');
                this.syncPromise = Promise.all([this.syncResumes(), this.syncSettings()]).then(([resumesSyncted, settingsSynced]) => resumesSyncted || settingsSynced);
                const synced = await this.syncPromise;
                this.setState(synced ? 'synced' : 'ready');
                this.syncPromise = undefined;
            }
            else {
                await this.syncPromise;
                this.sync();
            }
        }
        catch (e) {
            datadogRum.addError(e);
            this.setState('error');
            this.syncPromise = undefined;
        }
    }
    async syncResumes() {
        const localResumes = LocalStorageManagerV1.instance().getSystem().resumes;
        const remoteResumes = this.cachedRemoteResumes || {};
        const allResumeIds = new Set(Object.keys(localResumes).concat(Object.keys(remoteResumes)));
        const result = {};
        let uploaded = false;
        for (const resumeId of Array.from(allResumeIds)) {
            if (localResumes[resumeId] && remoteResumes[resumeId]) {
                const convertedLocalResume = this.localToRemoteResume(resumeId, localResumes[resumeId]);
                const remoteResume = remoteResumes[resumeId];
                const localModifiedAt = new Date(convertedLocalResume.lastModifiedDate);
                const remoteModifiedAt = new Date(remoteResume.lastModifiedDate);
                const patch = {};
                // Check if local resume is newer than local version
                if (remoteModifiedAt < localModifiedAt) {
                    Object.keys(convertedLocalResume).forEach((k) => {
                        if (!isEqual(convertedLocalResume[k], remoteResume[k])) {
                            // Typescript cannot deduce the correct type in cases like this
                            // https://github.com/microsoft/TypeScript/issues/43426
                            patch[k] = convertedLocalResume[k];
                        }
                    });
                }
                // Don't patch resume when only `lastModifiedDate` field has changed
                if (isEmpty(omit(patch, 'lastModifiedDate'))) {
                    result[resumeId] = remoteResume;
                }
                else {
                    result[resumeId] = await this.patchResume(resumeId, patch);
                    uploaded = true;
                }
            }
            else if (localResumes[resumeId]) {
                // upload local resume to remote server
                result[resumeId] = await this.postResume(this.localToRemoteResume(resumeId, localResumes[resumeId]));
                uploaded = true;
            }
            else if (remoteResumes[resumeId]) {
                // Check if we should delete resume in the future.
                // We should put a tombstone when deletes resume locally
                result[resumeId] = remoteResumes[resumeId];
            }
        }
        this.cachedRemoteResumes = result;
        const system = LocalStorageManagerV1.instance().getSystem();
        system.resumes = this.mergeLocalResult(localResumes, result);
        LocalStorageManagerV1.instance().saveSystem(system);
        return uploaded;
    }
    async syncSettings() {
        const localSettings = LocalStorageManagerV1.instance().getSystem().settings;
        const remoteSettings = this.cachedRemoteSettings;
        if (!localSettings) {
            if (!remoteSettings) {
                return false;
            }
            this.saveSettingsToLocal();
            return false;
        }
        if (!remoteSettings) {
            this.cachedRemoteSettings = await this.postSettings(localSettings);
            this.saveSettingsToLocal();
            return true;
        }
        if (localSettings.id !== remoteSettings.id) {
            this.saveSettingsToLocal();
            return false;
        }
        const localModifiedAt = new Date(localSettings.lastModifiedDate);
        const remoteModifiedAt = new Date(remoteSettings.lastModifiedDate);
        if (remoteModifiedAt > localModifiedAt) {
            this.saveSettingsToLocal();
            return false;
        }
        const modifiedKeys = Object.keys(localSettings)
            .filter((key) => key !== 'lastModifiedDate')
            .filter((key) => localSettings[key] !== remoteSettings[key]);
        if (modifiedKeys.length === 0) {
            return false;
        }
        const changedSettings = {
            id: remoteSettings.id
        };
        for (const key of modifiedKeys) {
            // Typescript cannot deduce the correct type in cases like this
            // https://github.com/microsoft/TypeScript/issues/43426
            changedSettings[key] = localSettings[key];
        }
        this.cachedRemoteSettings = await this.patchSettings(changedSettings);
        this.saveSettingsToLocal();
        return true;
    }
    saveSettingsToLocal() {
        if (this.cachedRemoteSettings) {
            const system = LocalStorageManagerV1.instance().getSystem();
            system.settings = { ...this.cachedRemoteSettings };
            LocalStorageManagerV1.instance().saveSystem(system);
        }
    }
    mergeLocalResult(localResumes, remoteResumes) {
        const merged = {};
        Object.keys(remoteResumes).forEach((resumeId) => {
            merged[resumeId] = this.remoteToLocalResume(remoteResumes[resumeId]);
            // UNIV-1403: uploading photo is disabled, prevent remote resume from overriding local photo
            if (localResumes[resumeId]) {
                merged[resumeId].photo = localResumes[resumeId].photo || '';
            }
            if (localResumes[resumeId] && localResumes[resumeId].lastWizardPath) {
                merged[resumeId].lastWizardPath = localResumes[resumeId].lastWizardPath;
            }
        });
        return merged;
    }
    async init() {
        await LocalStorageManagerV1.instance().ready();
        try {
            this.cachedRemoteResumes = await this.fetchRemoteResumes();
            this.cachedRemoteSettings = await this.fetchRemoteSettings();
            if (!this.cachedRemoteSettings) {
                await this.createInitialRemoteSettings();
            }
        }
        catch (e) {
            datadogRum.addError(e);
            this.setState('error');
        }
    }
    async createInitialRemoteSettings() {
        const localSettings = LocalStorageManagerV1.instance().getSystem().settings;
        if (localSettings) {
            await this.postSettings(localSettings);
        }
        this.cachedRemoteSettings = await this.fetchRemoteSettings();
        this.saveSettingsToLocal();
    }
    async fetchRemoteResumes() {
        const remoteResumes = await this.apiRequest(API_RESUME_ROOT, 'GET');
        const remoteResumeMap = {};
        remoteResumes.forEach((resume) => (remoteResumeMap[resume.resumeId] = resume));
        return remoteResumeMap;
    }
    async postResume(resume) {
        return this.apiRequest(API_RESUME_ROOT, 'POST', resume);
    }
    async patchResume(id, resume) {
        return this.apiRequest(`${API_RESUME_ROOT}${id}/`, 'PATCH', resume);
    }
    async fetchRemoteSettings() {
        const remoteSettings = await this.apiRequest(API_SETTINGS_ROOT, 'GET');
        return remoteSettings[0];
    }
    async postSettings(settings) {
        return this.apiRequest(API_SETTINGS_ROOT, 'POST', settings);
    }
    async patchSettings(settings) {
        return this.apiRequest(`${API_SETTINGS_ROOT}${settings.id}/`, 'PATCH', settings);
    }
    convertToDateStr(year, month, date) {
        const intYear = parseInt(year, 10) || 2020;
        const intMonth = parseInt(month, 10) || 1;
        const intDate = parseInt(date, 10) || 1;
        return `${intYear}-${intMonth < 10 ? `0${intMonth}` : intMonth}-${intDate < 10 ? `0${intDate}` : intDate}`;
    }
    convertToYearMonthDay(dateStr) {
        const date = new Date(dateStr || '2020-01-01');
        return {
            year: date.getFullYear().toString(),
            month: (date.getMonth() + 1).toString(),
            day: date.getDate().toString()
        };
    }
    localToRemoteResume(resumeId, localResume) {
        return {
            resumeId,
            // Meta
            type: localResume.type,
            resumeName: localResume.resumeName || '',
            companyName: localResume.companyName || '',
            creationDate: this.convertToDateStr(localResume.creationDate.year, localResume.creationDate.month, localResume.creationDate.day),
            isCreationDateEmpty: localResume.creationDate.isEmpty,
            isCreationDateCustomDate: localResume.creationDate.isCustomDate,
            isCreationDateLastModifiedDate: localResume.creationDate.isLastModifiedDate,
            lastModifiedDate: localResume.lastModifiedDate,
            // BasicInfo
            lastName: localResume.basicInfo.lastName,
            lastNameKana: localResume.basicInfo.lastNameKana,
            firstName: localResume.basicInfo.firstName,
            firstNameKana: localResume.basicInfo.firstNameKana,
            birthDate: this.convertToDateStr(localResume.basicInfo.birthYear, localResume.basicInfo.birthMonth, localResume.basicInfo.birthDay),
            // Deprecated field
            gender: localResume.basicInfo.gender,
            genderString: localResume.basicInfo.genderString,
            email: localResume.basicInfo.email,
            // Contact
            ...localResume.contact,
            // Guardian
            guardianFullName: localResume.type === 'parttime' && localResume.guardian
                ? localResume.guardian.fullName
                : '',
            guardianFullNameKana: localResume.type === 'parttime' && localResume.guardian
                ? localResume.guardian.fullNameKana
                : '',
            guardianZip: localResume.type === 'parttime' && localResume.guardian
                ? localResume.guardian.zip
                : '',
            guardianAddress: localResume.type === 'parttime' && localResume.guardian
                ? localResume.guardian.address
                : '',
            guardianAddressKana: localResume.type === 'parttime' && localResume.guardian
                ? localResume.guardian.addressKana
                : '',
            guardianBuildingName: localResume.type === 'parttime' && localResume.guardian
                ? localResume.guardian.buildingName
                : '',
            guardianPhone: localResume.type === 'parttime' && localResume.guardian
                ? localResume.guardian.phone
                : '',
            guardianEmail: localResume.type === 'parttime' && localResume.guardian
                ? localResume.guardian.email
                : '',
            // Free answers
            preference: localResume.preference,
            selfPromotion: localResume.selfPromotion,
            motivation: localResume.motivation,
            workSummary: localResume.type !== 'parttime' ? localResume.workSummary : '',
            // photo
            // UNIV-1403: disable photo uploading
            // photo: localResume.photo,
            photo: '',
            // Nested fields
            education: cloneDeep(localResume.education),
            certifications: cloneDeep(localResume.certifications),
            employment: cloneDeep(localResume.employment),
            skills: cloneDeep(localResume.type !== 'parttime' ? localResume.skills : []),
            languages: cloneDeep(localResume.type !== 'parttime' ? localResume.languages : [])
        };
    }
    remoteToLocalResume(remoteResume) {
        const birthDate = this.convertToYearMonthDay(remoteResume.birthDate || '');
        const creationDate = this.convertToYearMonthDay(remoteResume.creationDate || '');
        creationDate.isEmpty = remoteResume.isCreationDateEmpty || false;
        creationDate.isCustomDate = remoteResume.isCreationDateCustomDate || false;
        creationDate.isLastModifiedDate =
            remoteResume.isCreationDateLastModifiedDate ||
                (!creationDate.isEmpty && !creationDate.isCustomDate);
        const formValues = {
            resumeName: remoteResume.resumeName,
            companyName: remoteResume.companyName,
            basicInfo: {
                lastName: remoteResume.lastName,
                lastNameKana: remoteResume.lastNameKana,
                firstName: remoteResume.firstName,
                firstNameKana: remoteResume.firstNameKana,
                birthYear: birthDate.year,
                birthMonth: birthDate.month,
                birthDay: birthDate.day,
                gender: remoteResume.gender,
                genderString: remoteResume.genderString,
                email: remoteResume.email
            },
            contact: {
                zip: remoteResume.zip,
                address: remoteResume.address,
                addressKana: remoteResume.addressKana,
                buildingName: remoteResume.buildingName,
                phone: remoteResume.phone,
                phoneMobile: remoteResume.phoneMobile,
                preferZip: remoteResume.preferZip,
                preferAddress: remoteResume.preferAddress,
                preferAddressKana: remoteResume.preferAddressKana,
                preferBuildingName: remoteResume.preferBuildingName,
                preferPhone: remoteResume.preferPhone,
                preferPhoneMobile: remoteResume.preferPhoneMobile,
                sameAsResidence: remoteResume.sameAsResidence
            },
            photo: remoteResume.photo,
            preference: remoteResume.preference,
            selfPromotion: remoteResume.selfPromotion,
            motivation: remoteResume.motivation,
            creationDate,
            lastModifiedDate: remoteResume.lastModifiedDate,
            education: cloneDeep(remoteResume.education),
            certifications: cloneDeep(remoteResume.certifications)
        };
        if (remoteResume.type === 'parttime') {
            const guardian = {
                fullName: remoteResume.guardianFullName,
                fullNameKana: remoteResume.guardianFullNameKana,
                zip: remoteResume.guardianZip,
                address: remoteResume.guardianAddress,
                addressKana: remoteResume.guardianAddressKana,
                buildingName: remoteResume.guardianBuildingName,
                phone: remoteResume.guardianPhone,
                email: remoteResume.guardianEmail
            };
            const employment = remoteResume.employment;
            const res = {
                type: 'parttime',
                guardian,
                employment,
                ...formValues
            };
            return res;
        }
        else {
            const workSummary = remoteResume.workSummary;
            const employment = cloneDeep(remoteResume.employment);
            const skills = cloneDeep(remoteResume.skills);
            const languages = cloneDeep(remoteResume.languages);
            const res = {
                type: remoteResume.type,
                workSummary,
                employment,
                skills,
                languages,
                ...formValues
            };
            return res;
        }
    }
}
